import type { StudentsData } from "@/artifacts/admin/pages/Dashboard";
import { buildStudentsRows } from "@/artifacts/admin/utils/dashboard/buildStudentsRows";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Input } from "@/components/ui/Input/Input";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import { numberInStringArrayFilter } from "@/utils/filters";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Filter,
  UserRoundCog,
} from "lucide-react";
import { useMemo, useState } from "react";

export type StudentsRow = {
  _id: string;
  name: string;
  email: string;
  gradeLevel: number;
};

const buildColumns = (
  onOpen: (studentId: string) => void,
): ColumnDef<StudentsRow>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Student{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Email{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "gradeLevel",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grade Level{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: numberInStringArrayFilter,
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon-small" onClick={() => onOpen(row.original._id)}>
              <UserRoundCog />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Manage {row.original.name}</TooltipContent>
        </Tooltip>
      </HStack>
    ),
    enableSorting: false,
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: StudentsRow[];
  columns: ColumnDef<StudentsRow>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  // Grade Filtering

  const gradeCol = table.getColumn("gradeLevel");
  const selectedGrade =
    (gradeCol?.getFilterValue() as string[] | undefined) ?? [];

  const grades = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.gradeLevel) set.add(String(d.gradeLevel));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleGrade = (grade: string) => {
    const next = selectedGrade.includes(grade)
      ? selectedGrade.filter((s) => s !== grade)
      : [...selectedGrade, grade];

    gradeCol?.setFilterValue(next);
  };

  const clearGradeFilter = () => {
    gradeCol?.setFilterValue([]);
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  // Pagination

  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;

    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);

    const items: (number | "…")[] = [1];

    if (c > 3) items.push("…");

    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);

    for (let p = start; p <= end; p++) items.push(p);

    if (c < t - 2) items.push("…");

    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {grades.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g}
                      checked={selectedGrade.includes(g)}
                      onCheckedChange={() => toggleGrade(g)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {g}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearGradeFilter();
                    }}
                    disabled={selectedGrade.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} students
          </p>
        </HStack>
        <HStack align="center">
          <div style={{ width: "240px", maxWidth: "100%" }}>
            <Input
              placeholder="Search students..."
              value={
                (table.getColumn("name")?.getFilterValue() as string) ?? ""
              }
              onChange={(e) =>
                table.getColumn("name")?.setFilterValue(e.target.value)
              }
            />
          </div>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => {
              table.setPageSize(Number(v));
              table.setPageIndex(0);
            }}
          >
            <SelectTrigger
              style={{
                width: "fit-content",
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <SelectValue placeholder="Select results per page" />
            </SelectTrigger>
            <SelectContent>
              {RESULTS_PER_PAGE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option} results/page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>

            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const StudentsTable = ({
  students,
  onOpen,
}: {
  students: StudentsData[];
  onOpen: (studentId: string) => void;
}) => {
  const rows = useMemo(() => buildStudentsRows(students), [students]);
  const columns = useMemo(() => buildColumns(onOpen), [onOpen]);

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { StudentsTable };
